/**
 * Firebase auth for the site's shared login form.
 * New accounts upgrade the anonymous user in place; existing-account login
 * uses that account's data. Google uses a popup on desktop and redirect on mobile. Apple uses its web SDK.
 * Redirect results are consumed by accountAuth.ts.
 */

import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  linkWithCredential,
  linkWithPopup,
  linkWithRedirect,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  updateProfile,
  type Auth,
  type AuthCredential,
  type UserCredential,
} from 'firebase/auth';

import { auth as defaultAuth } from '../../firebaseConfig';
import { isMobileUserAgent, type RedirectOperation } from './accountAuth';
import { sendAccountVerification } from './emailVerification';
import type { AppleSignInRequest } from './appleWebSignIn';
import { resolveAppleAccount } from '../callables';

/**
 * The VERIFIED outcome of a direct auth (login-signin-or-create-account-v4-spec §4).
 * Threaded to the caller so the post-auth confirmation is driven by what ACTUALLY
 * happened — never by the entered email or the button tapped.
 *
 *   account 'created'  — a brand-new Thea account (anon upgraded in place, OR a
 *                        fresh create for a non-anon caller).
 *   account 'existing' — signed into an account that already existed.
 *   guestData 'linked' — the anon guest board was kept on the same uid (a link
 *                        succeeded). Emitted ONLY after Firebase reports success.
 *   guestData 'none'   — no guest board was carried over. This INCLUDES signing
 *                        into a pre-existing account, which by design leaves the
 *                        guest board behind (the One Rule — there is no merge path
 *                        here). `merged` therefore never occurs in this module.
 */
export type V4AuthAccount = 'created' | 'existing';
export type V4AuthGuestData = 'none' | 'linked';

export interface V4AuthOutcome {
  account: V4AuthAccount;
  guestData: V4AuthGuestData;
}

export interface V4AuthResult {
  verificationEmail?: 'sent' | 'failed';
  credential: UserCredential;
  outcome: V4AuthOutcome;
}

const CREATED_LINKED: V4AuthOutcome = { account: 'created', guestData: 'linked' };
const CREATED_ALONE: V4AuthOutcome = { account: 'created', guestData: 'none' };
const EXISTING_ALONE: V4AuthOutcome = { account: 'existing', guestData: 'none' };

// The SAME sessionStorage key accountAuth's consumeAuthRedirectResult reads on
// the return leg (accountAuth does not export it, so it is mirrored here). Written
// before a mobile redirect so `hasPendingAuthRedirect()` holds neutral UI while
// the credential finalizes; harmless if absent (consume calls getRedirectResult
// unconditionally), it just avoids a signed-out flash.
const REDIRECT_MARKER_KEY = 'thea:authRedirectStarted';

/**
 * `operation` records WHICH call we handed to the provider — the only thing on
 * the return leg that can tell a brand-new account from a returning one. A
 * successful `linkWithRedirect` means the identity was unknown to Firebase (an
 * identity already in use fails `credential-already-in-use`), so it created an
 * account; `getAdditionalUserInfo().isNewUser` still reports false because no
 * new account RECORD was minted. Without this field the return leg called a
 * first-time Google user a returning one (THE-712).
 */
function writeRedirectMarker(
  provider: 'google' | 'apple',
  operation: RedirectOperation,
): void {
  try {
    sessionStorage.setItem(
      REDIRECT_MARKER_KEY,
      JSON.stringify({ provider, operation, ts: Date.now() }),
    );
  } catch {
    /* Safari private / quota — the marker is diagnostic, not load-bearing */
  }
}

function credentialFromError(err: unknown): AuthCredential | null {
  type In = Parameters<typeof OAuthProvider.credentialFromError>[0];
  return (
    OAuthProvider.credentialFromError(err as In) ??
    GoogleAuthProvider.credentialFromError(err as In)
  );
}

function providerKey(p: GoogleAuthProvider | OAuthProvider): 'google' | 'apple' {
  return p instanceof GoogleAuthProvider ? 'google' : 'apple';
}

/**
 * Generic OAuth: popup on desktop, redirect on mobile. Anon → link (preserve the
 * board). When the OAuth account already belongs to a real user the link fails
 * `credential-already-in-use`; we recover the credential and sign into that
 * account (board left behind). Returns null when a mobile redirect was initiated
 * (the flow resumes via accountAuth.consumeAuthRedirectResult on return).
 */
async function signInWithOAuthProvider(
  provider: GoogleAuthProvider | OAuthProvider,
  authInstance: Auth,
): Promise<V4AuthResult | null> {
  const mobile = isMobileUserAgent();
  const anon = authInstance.currentUser?.isAnonymous ? authInstance.currentUser : null;

  if (anon) {
    try {
      if (mobile) {
        writeRedirectMarker(providerKey(provider), 'link');
        await linkWithRedirect(anon, provider);
        return null;
      }
      // Link succeeded → the provider identity was new to Thea, so a new account
      // was created in place and the guest board is preserved on the same uid.
      const credential = await linkWithPopup(anon, provider);
      return { credential, outcome: CREATED_LINKED };
    } catch (err) {
      if ((err as { code?: string })?.code === 'auth/credential-already-in-use') {
        const cred = credentialFromError(err);
        if (cred) {
          // The OAuth identity already belongs to a real account → sign into it.
          // The guest board is left behind (the One Rule): guestData 'none'.
          const credential = await signInWithCredential(authInstance, cred);
          return { credential, outcome: EXISTING_ALONE };
        }
        // no recoverable credential → fall through to a fresh sign-in
      } else {
        throw err;
      }
    }
  }

  if (mobile) {
    writeRedirectMarker(providerKey(provider), 'signin');
    await signInWithRedirect(authInstance, provider);
    return null;
  }
  // Non-anon (or unrecoverable) popup sign-in — no guest board in play. Use the
  // provider's ACTUAL new-user result, not the button, to name created vs. existing.
  const credential = await signInWithPopup(authInstance, provider);
  const isNewUser = getAdditionalUserInfo(credential)?.isNewUser ?? false;
  return { credential, outcome: isNewUser ? CREATED_ALONE : EXISTING_ALONE };
}

export function v4SignInWithGoogle(
  authInstance: Auth = defaultAuth,
): Promise<V4AuthResult | null> {
  return signInWithOAuthProvider(new GoogleAuthProvider(), authInstance);
}

export async function v4SignInWithApple(
  request: AppleSignInRequest,
  authInstance: Auth = defaultAuth,
  existingAccount = false,
): Promise<V4AuthResult> {
  const guest = authInstance.currentUser?.isAnonymous ? authInstance.currentUser : null;
  const { idToken, rawNonce, displayName } = await request.signIn();
  const appleCredential = new OAuthProvider('apple.com').credential({ idToken, rawNonce });
  let result: V4AuthResult;
  let shouldLink = false;
  if (guest && !existingAccount) {
    const route = (await resolveAppleAccount({ idToken, rawNonce })).data.action;
    if (route !== 'link' && route !== 'signIn') throw new Error('Invalid Apple account route');
    shouldLink = route === 'link';
  }
  if (guest && shouldLink) {
    try {
      const credential = await linkWithCredential(guest, appleCredential);
      result = { credential, outcome: CREATED_LINKED };
    } catch (error) {
      if ((error as { code?: string }).code !== 'auth/credential-already-in-use') throw error;
      // Production consumes Apple's nonce even when linking fails. Neither the
      // original nor error-returned token may be reused. The UI requests another
      // explicit Apple authorization with a fresh nonce, then signs in directly.
      throw Object.assign(new Error('Authorize Apple again to sign into the existing account'), {
        code: 'auth/apple-fresh-sign-in-required',
      });
    }
  } else {
    const credential = await signInWithCredential(authInstance, appleCredential);
    result = { credential, outcome: getAdditionalUserInfo(credential)?.isNewUser ? CREATED_ALONE : EXISTING_ALONE };
  }
  // Apple supplies the name only on first consent. A profile write failure must
  // not report that a completed authentication failed.
  if (displayName && !result.credential.user.displayName) {
    await updateProfile(result.credential.user, { displayName }).catch(() => undefined);
  }
  return result;
}

/** Log in only. A failed login must never silently create an account.
 * https://firebase.google.com/docs/auth/web/password-auth
 */
export async function v4SignInWithEmailPassword(
  email: string,
  password: string,
  authInstance: Auth = defaultAuth,
): Promise<V4AuthResult> {
  const credential = await signInWithEmailAndPassword(authInstance, email, password);
  return { credential, outcome: EXISTING_ALONE };
}

/** Create an account, upgrading the guest in place so their boards stay attached.
 * https://firebase.google.com/docs/auth/web/anonymous-auth
 */
export async function v4CreateAccountWithEmailPassword(
  email: string,
  password: string,
  authInstance: Auth = defaultAuth,
): Promise<V4AuthResult> {
  const guest = authInstance.currentUser;
  if (guest?.isAnonymous) {
    const credential = await linkWithCredential(
      guest, EmailAuthProvider.credential(email, password),
    );
    return { credential, outcome: CREATED_LINKED, verificationEmail: await sendAccountVerification(credential.user) };
  }
  const credential = await createUserWithEmailAndPassword(authInstance, email, password);
  return { credential, outcome: CREATED_ALONE, verificationEmail: await sendAccountVerification(credential.user) };
}

/** Firebase password reset (enumeration-safe: enableImprovedEmailPrivacy is on). */
export function v4SendPasswordReset(
  email: string,
  authInstance: Auth = defaultAuth,
): Promise<void> {
  return sendPasswordResetEmail(authInstance, email);
}
